import React from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { Modal, Button, Accordion, Table } from '@morressier/alembic';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope } from '@fortawesome/pro-light-svg-icons';
import {
  IPeerReviewFirstAssessment,
  IPeerReviewRevisionAssessment
} from '@publishing-engine/domain/models';
import { recommendationBadges } from '../Review/Badges';
import { faFile } from '@fortawesome/pro-regular-svg-icons';
import { IFetchOrganizationAndJournalsInfo } from 'src/api/queries';
import BetterSuitedForAnotherJournal from '../Submission/ReviewTab/BetterSuitedForAnotherJournal';
import { MissingReference } from '../Submission/ReviewTab/MissingRefrence';
import FileViewerButtonText from '../Submission/FileViewerButtonText';
import {
  DisplayField,
  DisplayFieldFromForm
} from 'src/components/shared/DisplayField';

type IFormValues = IPeerReviewFirstAssessment | IPeerReviewRevisionAssessment;

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void;
  formValues: IFormValues;
  formConfigs: any;
  organizationData: IFetchOrganizationAndJournalsInfo;
  submissionContextId: string;
  reviewerAnonymity?: string | undefined;
  peerReviewTransparency?: string | undefined;
}

export const submissionScoreTableContent = (
  formValues: IFormValues,
  formConfigs: any
) => {
  const submissionScoreKeys = Object.keys(formValues.submissionScores!);

  const cellsContent = submissionScoreKeys.map((key, index) => {
    const keyInCamelCase = key.charAt(0).toUpperCase() + key.slice(1);
    const field = formConfigs[keyInCamelCase];
    const dataValue = formValues.submissionScores
      ? formValues.submissionScores[key]
      : '';
    const scoreText = dataValue
      ? field?.params?.labels?.options[dataValue]
      : {
          main: '',
          secondary: ''
        };

    return (
      <Table.Row key={index}>
        <Table.RowCell flex="1" textStyle="headingLine" textAlign="left">
          {field?.name}
        </Table.RowCell>
        <Table.RowCell flex="1" textAlign="left">
          <Text textStyle="headingItem">{scoreText?.main}</Text>
          <Text textStyle="textContext">{scoreText?.secondary}</Text>
        </Table.RowCell>
      </Table.Row>
    );
  });
  return cellsContent;
};

const feedbackAccordionContent = (
  formValues: IFormValues,
  formConfigs: any,
  organizationData: IFetchOrganizationAndJournalsInfo,
  submissionContextId: string
) => {
  return (
    <Flex
      gap={6}
      background="whiteSecondaryFill"
      flexDirection="column"
      padding={4}
    >
      {formValues?.submissionScores && formConfigs && (
        <Box flexDirection="column">
          <Text textStyle="headingLine">Submission Scores</Text>
          <Box marginX={2} marginTop={4}>
            <Table>
              <Table.HeaderRow>
                <Table.HeaderCell flex="1" textAlign="left">
                  Criteria
                </Table.HeaderCell>
                <Table.HeaderCell flex="1" textAlign="left">
                  Score
                </Table.HeaderCell>
              </Table.HeaderRow>
              <Table.Body sx={{ borderTop: 'none' }}>
                <>{submissionScoreTableContent(formValues, formConfigs)}</>
              </Table.Body>
            </Table>
          </Box>
        </Box>
      )}

      <Flex direction="column" gap={4}>
        <Text textStyle="headingGroup">For Authors</Text>
        {formValues?.feedbackForAuthors?.original && (
          <Flex flexDirection="column" gap={2}>
            <Text textStyle="headingItem">
              {formConfigs.FeedbackForAuthors.params.labels.main ||
                'Feedback for Authors'}
            </Text>
            <Text textStyle="textBody">
              {formValues.feedbackForAuthors.original}
            </Text>
          </Flex>
        )}
        {formValues?.reviewerAttachments?.length ? (
          <Flex flexDirection="column" gap={2}>
            <Text textStyle="headingItem">
              {formConfigs.ReviewerAttachments.params.labels.main ||
                'Review Attachments'}
            </Text>

            <Flex flexDirection="column" gap={2}>
              {formValues.reviewerAttachments.map((attachment, index) => (
                <Flex key={index} gap={2} alignItems="center">
                  <FontAwesomeIcon icon={faFile} />
                  <Text
                    textStyle="textBody"
                    wordBreak="break-word"
                    whiteSpace="normal"
                    flex={1}
                    minWidth={0}
                  >
                    {attachment.name}
                  </Text>
                  <FileViewerButtonText
                    file={attachment}
                    submissionContextId={submissionContextId}
                    textStyle="textBody"
                    flexShrink={0}
                  >
                    View
                  </FileViewerButtonText>
                </Flex>
              ))}
            </Flex>
          </Flex>
        ) : null}
      </Flex>
      <Flex direction="column" gap={3}>
        <Text textStyle="headingGroup">For Editors</Text>
        {formConfigs?.UnaddressedReviewComments &&
          formValues &&
          'unaddressedReviewComments' in formValues && (
            <DisplayFieldFromForm
              formValues={formValues as Record<string, any>}
              formConfigs={formConfigs}
              configField="UnaddressedReviewComments"
              valueField="unaddressedReviewComments"
            />
          )}

        {
          <MissingReference
            config={formConfigs?.MissingReference}
            missingReference={formValues?.missingReference || null}
          />
        }

        {formValues?.articleWellWritten && (
          <DisplayField
            fieldName="ArticleWellWritten"
            value={formValues.articleWellWritten}
            config={formConfigs}
          />
        )}

        {formValues?.feedbackForEditors && (
          <Flex flexDirection="column" gap={2}>
            <Text textStyle="headingItem">
              {formConfigs.FeedbackForEditors.params.labels.main ||
                'Feedback for Editor'}
            </Text>
            <Text textStyle="textBody">{formValues.feedbackForEditors}</Text>
          </Flex>
        )}

        {formValues?.reviewerAttachmentsEditor?.length ? (
          <Flex flexDirection="column" gap={2}>
            <Text textStyle="headingItem">
              {formConfigs.ReviewerAttachmentsEditor.params.labels.main ||
                'Reviewer Attachments For Editors'}
            </Text>

            <Flex flexDirection="column" gap={2}>
              {formValues.reviewerAttachmentsEditor.map((attachment, index) => (
                <Flex key={index} gap={2} alignItems="center">
                  <FontAwesomeIcon icon={faFile} />
                  <Text
                    textStyle="textBody"
                    wordBreak="break-word"
                    whiteSpace="normal"
                    flex={1}
                    minWidth={0}
                  >
                    {attachment.name}
                  </Text>
                  <FileViewerButtonText
                    file={attachment}
                    submissionContextId={submissionContextId}
                    textStyle="textBody"
                    flexShrink={0}
                  >
                    View
                  </FileViewerButtonText>
                </Flex>
              ))}
            </Flex>
          </Flex>
        ) : null}

        {formValues &&
          'areaOfExpertise' in formValues &&
          formValues?.areaOfExpertise && (
            <DisplayField
              fieldName="AreaOfExpertise"
              value={formValues.areaOfExpertise}
              config={formConfigs}
            />
          )}

        {formConfigs?.BetterSuitedForAnotherJournal && (
          <BetterSuitedForAnotherJournal
            config={formConfigs?.BetterSuitedForAnotherJournal}
            betterSuitedForAnotherJournal={
              formValues?.betterSuitedForAnotherJournal || null
            }
            organizationData={organizationData}
          />
        )}
        {formValues?.conflictOfInterest && (
          <DisplayField
            fieldName="ConflictOfInterest"
            value={formValues.conflictOfInterest}
            config={formConfigs}
          />
        )}
        {formValues?.canArticleBeShortened && (
          <DisplayField
            fieldName="CanArticleBeShortened"
            value={formValues.canArticleBeShortened}
            config={formConfigs}
          />
        )}
        {formValues?.meritFurtherCoverageUponPublication && (
          <DisplayField
            fieldName="MeritFurtherCoverageUponPublication"
            value={formValues.meritFurtherCoverageUponPublication}
            config={formConfigs}
          />
        )}
      </Flex>
    </Flex>
  );
};

export const optInAccordionContent = (
  formValues: IFormValues,
  formConfigs: any,
  padding = 4,
  peerReviewTransparency?: string | undefined,
  reviewerAnonymity?: string | undefined
) => {
  return (
    <Flex
      gap={6}
      background="whiteSecondaryFill"
      flexDirection="column"
      padding={padding}
    >
      <DisplayFieldFromForm
        formValues={formValues as Record<string, any>}
        formConfigs={formConfigs}
        configField="WebOfScienceOptIn"
        valueField="webOfScienceOptIn"
      />
      {!peerReviewTransparency && (
        <DisplayFieldFromForm
          formValues={formValues as Record<string, any>}
          formConfigs={formConfigs}
          configField="PeerReviewTransparency"
          valueField="peerReviewTransparency"
        />
      )}
      {!reviewerAnonymity && (
        <DisplayFieldFromForm
          formValues={formValues as Record<string, any>}
          formConfigs={formConfigs}
          configField="ReviewerAnonymity"
          valueField="reviewerAnonymity"
        />
      )}
      <DisplayFieldFromForm
        formValues={formValues as Record<string, any>}
        formConfigs={formConfigs}
        configField="ReceiveFeedback"
        valueField="receiveFeedback"
      />
    </Flex>
  );
};

export const consentAccordionContent = (
  formValues: IFormValues,
  formConfigs: any,
  padding = 4
) => {
  return (
    <Flex
      gap={6}
      background="whiteSecondaryFill"
      flexDirection="column"
      padding={padding}
    >
      <DisplayFieldFromForm
        formValues={formValues as Record<string, any>}
        formConfigs={formConfigs}
        configField="ConsentToReInvite"
        valueField="consentToReInvite"
      />
    </Flex>
  );
};

export const ReviewPreviewModal: React.FC<Props> = props => {
  const {
    isOpen,
    onClose,
    onSubmit,
    formValues,
    formConfigs,
    organizationData,
    submissionContextId,
    reviewerAnonymity,
    peerReviewTransparency
  } = props;

  const items = [
    {
      title: 'Your Feedback',
      content: feedbackAccordionContent(
        formValues,
        formConfigs,
        organizationData,
        submissionContextId
      )
    }
  ];

  // if WebOfScienceOptIn and ReceiveFeedback are present, add the opt-ins accordion
  const isWebOfScienceOptInAvailable =
    formValues &&
    'webOfScienceOptIn' in formValues &&
    formConfigs?.WebOfScienceOptIn;
  const isReceiveFeedbackAvailable =
    formValues &&
    'receiveFeedback' in formValues &&
    formConfigs?.ReceiveFeedback;

  const isConsentToReInvite =
    formValues &&
    'consentToReInvite' in formValues &&
    formConfigs?.ConsentToReInvite;

  if (isWebOfScienceOptInAvailable || isReceiveFeedbackAvailable) {
    items.push({
      title: 'Opt-Ins',
      content: optInAccordionContent(
        formValues,
        formConfigs,
        undefined,
        peerReviewTransparency,
        reviewerAnonymity
      )
    });
  }

  if (isConsentToReInvite) {
    items.push({
      title: 'Consent',
      content: consentAccordionContent(formValues, formConfigs, undefined)
    });
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      width="720px"
      closeButtonProps={{
        hidden: true
      }}
    >
      <Flex
        padding={8}
        gap={6}
        bgColor="surfacePrimaryFill"
        flexDirection="column"
        borderRadius="base"
      >
        <Flex justifyContent="flex-start">
          <FontAwesomeIcon
            icon={faEnvelope}
            style={{ height: '40px', width: '32px' }}
          />
        </Flex>
        <Flex flexDirection="column" gap={2}>
          <Text textStyle="headingGroup">Submit Review</Text>
          <Text textStyle="textBody">
            By proceeding, you are confirming that your Review is complete and
            accurate. Your recommendation, feedback, and attached files will be
            sent to the Editor and cannot be modified after Submission.
          </Text>
        </Flex>
        {formValues?.recommendation && (
          <Flex flexDirection="column" gap={2}>
            <Text textStyle="headingGroup">Recommendation</Text>
            <Flex>{recommendationBadges[formValues.recommendation]}</Flex>
          </Flex>
        )}
        <Flex flexDirection="column" gap={2}>
          <Accordion items={items} />
          <Flex
            gap={2}
            justifyContent="flex-end"
            alignItems="center"
            alignSelf="stretch"
          >
            <Button variant="secondary" onClick={onClose} borderRadius="small">
              Cancel
            </Button>
            <Button variant="primary" onClick={onSubmit} borderRadius="small">
              Submit Review
            </Button>
          </Flex>
        </Flex>
      </Flex>
    </Modal>
  );
};
